/**
 * AccordionShell — the canonical Wave Control Room accordion wrapper.
 *
 * Locked spec (Feb 2026 redesign · "5 accordion buckets"):
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ (●) 2  DRIVING TEAMS                              ▸          │
 *   │       12 teams · top 2 powering 68% of momentum              │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ (body — rendered only when `open` is true)                   │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Behavioral rules
 * ────────────────
 * • Hero (#1 Campaign Performance) is always-open, non-collapsible
 *   (`alwaysOpen={true}` + `collapsible={false}`).
 * • Other accordions are single-open by default (parent enforces by
 *   tracking which accordion id is open).
 * • Disabled accordions render in muted state with a lock icon and
 *   the user can't click through (Cohort Post-Mortem until ≥80%).
 *
 * Props
 * ─────
 *   id          string · stable identifier for parent's open-state
 *   index       number · the 1-5 numeral shown in the round badge
 *   title       string · UPPERCASE label
 *   chip        ReactNode · the one-line summary chip on the right of
 *                 the title (e.g. "12 teams · top 2 powering 68%")
 *   open        boolean · controlled open state from parent
 *   onToggle    () => void · toggle handler (no-op when alwaysOpen)
 *   alwaysOpen  boolean · render with `open` ignored
 *   disabled    boolean · greyed-out, click is no-op, shows lock icon
 *   children    body
 */
import React from 'react';
import { ChevronRight, Lock } from 'lucide-react';

export default function AccordionShell({
  id,
  index,
  title,
  chip = null,
  open = false,
  onToggle = () => {},
  alwaysOpen = false,
  disabled = false,
  children,
}) {
  const isOpen = alwaysOpen || open;
  const interactive = !alwaysOpen && !disabled;
  const handleClick = () => { if (interactive) onToggle(); };

  return (
    <section
      data-testid={`wcr-accordion-${id}`}
      data-open={isOpen ? 'true' : 'false'}
      className={`rounded-[18px] overflow-hidden transition-colors ${disabled ? 'opacity-60' : ''}`}
      style={{
        background: '#181A20',
        border: '1px solid #2E3240',
      }}
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={!interactive}
        aria-expanded={isOpen}
        data-testid={`wcr-accordion-${id}-toggle`}
        className={`w-full flex items-center justify-between gap-4 px-5 py-4 text-left ${interactive ? 'cursor-pointer hover:bg-white/[0.02]' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-3.5 min-w-0">
          {/* Numeric badge — purple-on-dark when open, muted when closed */}
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black flex-shrink-0"
            style={{
              background: isOpen ? '#A78BFA' : '#0F0F0F',
              color:      isOpen ? '#0a0a0a' : '#9aa1ad',
              border:     isOpen ? '1px solid #A78BFA' : '1px solid #2E3240',
            }}
          >
            {index}
          </span>
          <div className="min-w-0">
            <h3 className="text-[13.5px] font-black text-white uppercase tracking-[0.06em] leading-tight">
              {title}
            </h3>
            {chip && (
              <p
                className="text-[11px] text-gray-400 mt-0.5 leading-tight truncate"
                data-testid={`wcr-accordion-${id}-chip`}
              >
                {chip}
              </p>
            )}
          </div>
        </div>
        <span className="flex-shrink-0 text-gray-500">
          {disabled
            ? <Lock className="h-4 w-4" />
            : <ChevronRight
                className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90 text-white' : ''}`}
              />}
        </span>
      </button>

      {isOpen && !disabled && (
        <div className="px-5 pb-5 pt-1" data-testid={`wcr-accordion-${id}-body`}>
          {children}
        </div>
      )}
    </section>
  );
}
